// src/components/SectionsPicker.tsx
// Выбор доступных разделов для пользователя (по модулям)
import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Check, X, CheckSquare, Square } from 'lucide-react';
import api from '../api/client';

interface Props {
  userId: string;
  onSaved?: () => void;
}

const SectionsPicker: React.FC<Props> = ({ userId, onSaved }) => {
  const [modules, setModules] = useState<any[]>([]);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [metaR, userR] = await Promise.all([
          api.get('/permissions/sections'),
          api.get('/permissions/user/' + userId),
        ]);
        setModules(metaR.data.modules || []);
        setAllowed(userR.data.allowedSections || []);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [userId]);

  const toggle = (id: string) => {
    setAllowed(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setSaved(false);
  };

  const selectAll = (moduleId: string) => {
    const m = modules.find(x => x.id === moduleId);
    if (!m) return;
    const ids = m.sections.map((s: any) => s.id);
    setAllowed(prev => Array.from(new Set([...prev, ...ids])));
    setSaved(false);
  };

  const clearAll = (moduleId: string) => {
    const m = modules.find(x => x.id === moduleId);
    if (!m) return;
    const ids = new Set(m.sections.map((s: any) => s.id));
    setAllowed(prev => prev.filter(x => !ids.has(x)));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/permissions/user/' + userId + '/sections', { allowedSections: allowed });
      setSaved(true);
      onSaved?.();
    } catch (e: any) {
      alert('Ошибка: ' + (e?.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: '#8b5cf6' }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{
        padding: 12, marginBottom: 16, borderRadius: 12,
        background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
        fontSize: 12, color: '#475569', lineHeight: 1.6,
      }}>
        <b>ℹ️ Как работает:</b> отметьте разделы, к которым пользователь имеет доступ.
        Если ничего не отмечено — при включённом модуле видны <b>все</b> разделы.
        Администратор и супер-админ видят всё.
      </div>

      {modules.map(m => {
        const ids = m.sections.map((s: any) => s.id);
        const checkedCount = ids.filter((id: string) => allowed.includes(id)).length;
        const allChecked = checkedCount === ids.length;
        const noneChecked = checkedCount === 0;

        return (
          <div key={m.id} style={{
            marginBottom: 16, padding: 16, borderRadius: 14,
            background: '#fff', border: '1px solid #e2e8f0',
          }}>
            {/* Заголовок модуля */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>{m.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e' }}>{m.label}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Отмечено: {checkedCount} из {ids.length}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => selectAll(m.id)} disabled={allChecked} style={btnMini}>
                  <CheckSquare size={11} /> Все
                </button>
                <button onClick={() => clearAll(m.id)} disabled={noneChecked} style={btnMini}>
                  <Square size={11} /> Снять
                </button>
              </div>
            </div>

            {/* Сетка разделов */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
              {m.sections.map((s: any) => {
                const checked = allowed.includes(s.id);
                return (
                  <div key={s.id} onClick={() => toggle(s.id)} style={{
                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                    background: checked ? 'rgba(139,92,246,0.06)' : 'rgba(0,0,0,0.02)',
                    border: checked ? '1px solid rgba(139,92,246,0.2)' : '1px solid transparent',
                    display: 'flex', alignItems: 'center', gap: 8,
                    userSelect: 'none', transition: 'all 0.15s',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: checked ? '2px solid #8b5cf6' : '2px solid #cbd5e1',
                      background: checked ? '#8b5cf6' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 10, fontWeight: 700,
                    }}>
                      {checked ? <Check size={10} /> : null}
                    </div>
                    <span style={{ fontSize: 12 }}>{s.icon}</span>
                    <span style={{ fontSize: 12, color: checked ? '#1a1a2e' : '#64748b', fontWeight: checked ? 600 : 500 }}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Кнопка сохранения */}
      <div style={{ position: 'sticky', bottom: 0, padding: '16px 0', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        {saved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontSize: 13, fontWeight: 600 }}>
            <Check size={16} /> Сохранено
          </div>
        )}
        <button onClick={handleSave} disabled={saving} style={{
          padding: '10px 20px', border: 'none', borderRadius: 10,
          background: 'linear-gradient(135deg, #8b5cf6, #6a11cb)',
          color: '#fff', cursor: saving ? 'wait' : 'pointer',
          fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          opacity: saving ? 0.6 : 1,
        }}>
          <Save size={14} /> {saving ? 'Сохранение...' : 'Сохранить доступы'}
        </button>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const btnMini: React.CSSProperties = {
  padding: '5px 10px', border: 'none', borderRadius: 6,
  background: 'rgba(139,92,246,0.08)', color: '#8b5cf6',
  cursor: 'pointer', fontSize: 10, fontWeight: 700, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};

export default SectionsPicker;
